import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminApi } from '@/services/superAdminApi';
import { useAuth } from '@/contexts/AuthContext';
import { Search, LogIn, Loader2, ShieldAlert, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  superadmin: 'Super-Admin',
  admin: 'Admin',
  trainer: 'Trainer',
  customer: 'Kunde',
  mieter: 'Mieter',
};

const ROLE_COLORS = {
  superadmin: 'bg-violet-500/15 text-violet-700',
  admin: 'bg-indigo-500/15 text-indigo-700',
  trainer: 'bg-sky-500/15 text-sky-700',
  customer: 'bg-emerald-500/15 text-emerald-700',
  mieter: 'bg-amber-500/15 text-amber-700',
};

export default function SupportLoginPage() {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.supportUsers(query || '');
      setUsers(data.users || []);
    } catch (e) {
      toast.error('Nutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const start = async (u) => {
    if (u.is_active === false) {
      toast.error('Konto ist deaktiviert');
      return;
    }
    setStartingId(u.id);
    try {
      const { data } = await superAdminApi.supportLoginStart(u.id);
      await startImpersonation(data);
      toast.success(`Support-Modus aktiv als ${data.user.email}`);
      // Zum passenden Bereich des Zielnutzers navigieren
      const role = data.user.role;
      if (role === 'customer') navigate('/portal');
      else if (role === 'trainer') navigate('/appointments');
      else if (role === 'mieter') navigate('/resources');
      else navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Support-Login fehlgeschlagen');
    } finally {
      setStartingId(null);
    }
  };

  return (
    <div data-testid="sa-support-login" className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Support-Login</h1>
        <p className="mt-1 text-sm text-slate-600">
          Melde dich zeitlich begrenzt (30 Min) als ein Nutzerkonto an, um ein Problem aus dessen Sicht zu lösen. Jeder Support-Login wird protokolliert.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-500/5 p-4">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <p className="text-xs text-slate-600">
          Im Support-Modus handelst du im Namen des Nutzers. Ein Banner zeigt dir die laufende Sitzung; mit „Support-Modus verlassen“ kehrst du jederzeit zurück. Super-Admin-Bereiche sind im Support-Modus gesperrt.
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nach Name oder E-Mail suchen…"
          data-testid="sa-support-search"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 py-16 text-slate-500">
          <UserCog className="h-8 w-8" />
          <span className="text-sm">Keine Nutzer gefunden.</span>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
            const inactive = u.is_active === false;
            return (
              <div
                key={u.id}
                data-testid="sa-support-user-row"
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-900">{name}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${ROLE_COLORS[u.role] || 'bg-slate-200 text-slate-700'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {inactive && <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700">inaktiv</span>}
                  </div>
                  <div className="truncate text-xs text-slate-500">{u.email}</div>
                </div>
                <button
                  onClick={() => start(u)}
                  disabled={startingId === u.id || inactive}
                  data-testid={`sa-support-login-btn-${u.id}`}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-400 disabled:opacity-50"
                >
                  {startingId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />}
                  Als Nutzer anmelden
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
